import { useState } from 'react';
import { useOAuthConfigs, useDeleteOAuthConfig, useUpdateOAuthConfig, useTestOAuthConnection } from '../../hooks/useOAuthConfigs';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { OAuthSetupWizard } from './OAuthSetupWizard';
import { OAuthConfigEditForm } from './OAuthConfigEditForm';
import { ProviderIcon } from './ProviderIcon';
import type { OAuthConfig, OAuthProvider } from '../../types/oauth';
import { PROVIDER_DISPLAY_NAMES, PROVIDER_DOCS } from '../../types/oauth';
import './OAuthProviderList.css';

interface OAuthProviderListProps {
  projectId: string;
  userRole?: 'viewer' | 'member' | 'admin' | 'owner';
}

export function OAuthProviderList({ projectId, userRole = 'member' }: OAuthProviderListProps) {
  const { data, isLoading, error } = useOAuthConfigs(projectId);
  const deleteConfig = useDeleteOAuthConfig();
  const updateConfig = useUpdateOAuthConfig();
  const testConnection = useTestOAuthConnection();

  const [setupProvider, setSetupProvider] = useState<OAuthProvider | null>(null);
  const [editingConfig, setEditingConfig] = useState<OAuthConfig | null>(null);
  const [providerToRemove, setProviderToRemove] = useState<OAuthConfig | null>(null);
  const [testingProvider, setTestingProvider] = useState<OAuthProvider | null>(null);
  const [testResult, setTestResult] = useState<{ provider: OAuthProvider; success: boolean; message?: string } | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    configured: true,
    available: true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const canManage = userRole === 'admin' || userRole === 'owner';

  const handleSetupProvider = (provider: OAuthProvider) => {
    setSetupProvider(provider);
  };

  const handleEditConfig = (config: OAuthConfig) => {
    setEditingConfig(config);
  };

  const handleRemoveConfig = async () => {
    if (!providerToRemove) return;
    try {
      await deleteConfig.mutateAsync({
        projectId,
        provider: providerToRemove.provider,
      });
      setProviderToRemove(null);
    } catch {
      // Error handled by mutation
    }
  };

  const handleToggleEnabled = async (config: OAuthConfig) => {
    if (!config.tested_at && !config.enabled) {
      setTestResult({
        provider: config.provider,
        success: false,
        message: 'Please test the connection before enabling this provider.',
      });
      return;
    }

    try {
      await updateConfig.mutateAsync({
        projectId,
        provider: config.provider,
        data: { enabled: !config.enabled },
      });
    } catch {
      // Error handled by mutation
    }
  };

  const handleTestConnection = async (provider: OAuthProvider) => {
    setTestingProvider(provider);
    setTestResult(null);
    try {
      await testConnection.mutateAsync({ projectId, provider });
      setTestResult({ provider, success: true, message: 'Connection successful!' });
    } catch (err) {
      setTestResult({
        provider,
        success: false,
        message: err instanceof Error ? err.message : 'Connection test failed',
      });
    } finally {
      setTestingProvider(null);
    }
  };

  const formatTestedAt = (testedAt: string | null) => {
    if (!testedAt) return 'Not tested';
    const date = new Date(testedAt);
    return `Tested ${date.toLocaleDateString()} at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  if (isLoading) {
    return (
      <div className="oauth-provider-list">
        <div className="oauth-provider-list__loading">
          <div className="spinner" />
          <p>Loading OAuth providers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="oauth-provider-list">
        <div className="oauth-provider-list__error">
          <p>Failed to load OAuth configurations</p>
          <p className="error-detail">{error.message}</p>
        </div>
      </div>
    );
  }

  const { configured = [], available = [] } = data || {};

  return (
    <div className="oauth-provider-list">
      <div className="oauth-provider-list__header">
        <div>
          <h3>OAuth Providers</h3>
          <p className="text-muted">
            Configure OAuth providers to enable social login for your users.
          </p>
        </div>
      </div>

      {/* Test Result Banner */}
      {testResult && (
        <div className={`oauth-test-result oauth-test-result--${testResult.success ? 'success' : 'error'}`}>
          <span className="oauth-test-result__icon">
            {testResult.success ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            )}
          </span>
          <span className="oauth-test-result__message">
            <strong>{PROVIDER_DISPLAY_NAMES[testResult.provider]}:</strong> {testResult.message}
          </span>
          <button
            type="button"
            className="oauth-test-result__dismiss"
            onClick={() => setTestResult(null)}
          >
            &times;
          </button>
        </div>
      )}

      {/* Configured Providers */}
      {configured.length > 0 && (
        <div className="oauth-provider-section">
          <button
            type="button"
            className="oauth-provider-section__header"
            onClick={() => toggleSection('configured')}
            aria-expanded={expandedSections.configured}
          >
            <div className="oauth-provider-section__header-left">
              <span className="oauth-provider-section__checkmark oauth-provider-section__checkmark--configured">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              <h4 className="oauth-provider-section__title">Configured Providers</h4>
              <span className="oauth-provider-section__count">{configured.length}</span>
            </div>
            <span className={`oauth-provider-section__chevron ${expandedSections.configured ? 'oauth-provider-section__chevron--expanded' : ''}`}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </span>
          </button>
          <div className={`oauth-provider-section__content ${expandedSections.configured ? 'oauth-provider-section__content--expanded' : ''}`}>
          <div className="oauth-provider-grid">
            {configured.map((config) => (
              <div key={config.provider} className="oauth-provider-card oauth-provider-card--configured">
                <div className="oauth-provider-card__header">
                  <div className="oauth-provider-card__icon">
                    <ProviderIcon provider={config.provider} />
                  </div>
                  <div className="oauth-provider-card__info">
                    <h5>{PROVIDER_DISPLAY_NAMES[config.provider]}</h5>
                    <span className={`oauth-status-badge oauth-status-badge--${config.enabled ? 'enabled' : 'disabled'}`}>
                      {config.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  {canManage && (
                    <label className="oauth-toggle">
                      <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={() => handleToggleEnabled(config)}
                        disabled={updateConfig.isPending}
                      />
                      <span className="oauth-toggle__slider" />
                    </label>
                  )}
                </div>

                <div className="oauth-provider-card__details">
                  <div className="oauth-provider-card__detail">
                    <span className="oauth-provider-card__label">Client ID</span>
                    <code className="oauth-provider-card__value">{config.client_id.slice(0, 20)}...</code>
                  </div>
                  <div className="oauth-provider-card__detail">
                    <span className="oauth-provider-card__label">Status</span>
                    <span className={`oauth-provider-card__value ${config.tested_at ? 'text-success' : 'text-warning'}`}>
                      {formatTestedAt(config.tested_at)}
                    </span>
                  </div>
                </div>

                {canManage && (
                  <div className="oauth-provider-card__actions">
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => handleTestConnection(config.provider)}
                      disabled={testingProvider === config.provider}
                    >
                      {testingProvider === config.provider ? 'Testing...' : 'Test'}
                    </button>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => handleEditConfig(config)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger btn--sm"
                      onClick={() => setProviderToRemove(config)}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          </div>
        </div>
      )}

      {/* Available Providers */}
      {canManage && available.filter((p) => !p.configured).length > 0 && (
        <div className="oauth-provider-section">
          <button
            type="button"
            className="oauth-provider-section__header"
            onClick={() => toggleSection('available')}
            aria-expanded={expandedSections.available}
          >
            <div className="oauth-provider-section__header-left">
              <span className="oauth-provider-section__checkmark">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <circle cx="12" cy="12" r="9" />
                </svg>
              </span>
              <h4 className="oauth-provider-section__title">
                {configured.length > 0 ? 'Add More Providers' : 'Available Providers'}
              </h4>
              <span className="oauth-provider-section__count">{available.filter((p) => !p.configured).length}</span>
            </div>
            <span className={`oauth-provider-section__chevron ${expandedSections.available ? 'oauth-provider-section__chevron--expanded' : ''}`}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
              </svg>
            </span>
          </button>
          <div className={`oauth-provider-section__content ${expandedSections.available ? 'oauth-provider-section__content--expanded' : ''}`}>
          <div className="oauth-provider-grid">
            {available
              .filter((p) => !p.configured)
              .map((provider) => (
                <div key={provider.provider} className="oauth-provider-card oauth-provider-card--available">
                  <div className="oauth-provider-card__header">
                    <div className="oauth-provider-card__icon">
                      <ProviderIcon provider={provider.provider} />
                    </div>
                    <div className="oauth-provider-card__info">
                      <h5>{PROVIDER_DISPLAY_NAMES[provider.provider]}</h5>
                      <span className="oauth-status-badge oauth-status-badge--not-configured">
                        Not configured
                      </span>
                    </div>
                  </div>
                  <div className="oauth-provider-card__description">
                    <p>{getProviderDescription(provider.provider)}</p>
                  </div>
                  <div className="oauth-provider-card__actions">
                    <a
                      href={PROVIDER_DOCS[provider.provider]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn--secondary btn--sm"
                    >
                      Docs
                    </a>
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      onClick={() => handleSetupProvider(provider.provider)}
                    >
                      Configure
                    </button>
                  </div>
                </div>
              ))}
          </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {configured.length === 0 && !canManage && (
        <div className="oauth-provider-list__empty">
          <p>No OAuth providers configured for this project.</p>
          <p className="text-muted">Contact an admin to set up OAuth authentication.</p>
        </div>
      )}

      {/* Setup Wizard Modal */}
      {setupProvider && (
        <OAuthSetupWizard
          projectId={projectId}
          provider={setupProvider}
          onClose={() => setSetupProvider(null)}
          onSuccess={() => setSetupProvider(null)}
        />
      )}

      {/* Edit Modal */}
      {editingConfig && (
        <OAuthConfigEditForm
          projectId={projectId}
          config={editingConfig}
          onClose={() => setEditingConfig(null)}
          onSuccess={() => setEditingConfig(null)}
        />
      )}

      {/* Remove Confirmation */}
      {providerToRemove && (
        <ConfirmDialog
          title="Remove OAuth Provider"
          message={`Are you sure you want to remove ${PROVIDER_DISPLAY_NAMES[providerToRemove.provider]}? Users will no longer be able to sign in with this provider.`}
          confirmLabel="Remove"
          confirmVariant="danger"
          isLoading={deleteConfig.isPending}
          onConfirm={handleRemoveConfig}
          onCancel={() => setProviderToRemove(null)}
        />
      )}
    </div>
  );
}

function getProviderDescription(provider: OAuthProvider): string {
  switch (provider) {
    case 'google':
      return 'Allow users to sign in with their Google accounts.';
    case 'github':
      return 'Allow users to sign in with their GitHub accounts.';
    case 'microsoft':
      return 'Allow users to sign in with Microsoft 365 or Azure AD accounts.';
    case 'okta':
      return 'Allow users to sign in with their Okta organization accounts.';
    case 'apple':
      return 'Allow users to sign in with their Apple ID.';
    default:
      return '';
  }
}
